// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

/**
 * "Copy tag" affordance (PRD OQ-6): copies a `Relationship:{id}` /
 * `Client:{id}` token to the clipboard so advisors can tag calendar events
 * (description) and emails (Gmail label / subject) without ever typing a
 * UUID. See docs/guides/advisor-tagging.md.
 */

import { IconButton, AppIcon, Tooltip, useOptionalToast } from '@loa/ui-components';

interface CopyTagButtonProps {
  entityKind: 'Relationship' | 'Client';
  entityId: string;
}

export function CopyTagButton({ entityKind, entityId }: Readonly<CopyTagButtonProps>) {
  // Tolerate a missing ToastProvider (isolated page tests render without
  // one) — the copy still works, only the confirmation is skipped.
  const toast = useOptionalToast();
  const showToast = toast?.showToast;
  const tag = `${entityKind}:${entityId}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(tag);
      showToast?.(`Tag copied — paste it on a calendar event or Gmail label to link it to this ${entityKind.toLowerCase()}.`, 'success');
    } catch {
      showToast?.('Could not access the clipboard.', 'error');
    }
  };

  return (
    <Tooltip
      content={`Copy "${tag}" for tagging emails and meetings`}
      trigger={(
        <IconButton
          aria-label={`Copy ${entityKind.toLowerCase()} tag`}
          variant="ghost"
          size="xs"
          icon={<AppIcon name="copy" size={16} />}
          onClick={() => { void copy(); }}
        />
      )}
    />
  );
}
